import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Lock as LockIcon,
  PictureAsPdf as PdfIcon,
  MusicNote as MusicNoteIcon,
  School as SchoolIcon,
  Chat as ChatIcon
} from '@mui/icons-material';

const API_BASE_URL = 'https://api.labs.jeshua.ong';

const getSite = () => {
  const hostname = window.location.hostname;
  if (hostname.includes('jeshua.ong')) return 'jeshua.ong';
  if (hostname.includes('jeshua.es')) return 'jeshua.es';
  if (hostname.includes('jeshua.cl')) return 'jeshua.cl';
  return 'jeshua.ong';
};

const LockedAction = ({ icon, label, tooltip }) => (
  <Tooltip title={tooltip} arrow>
    <span>
      <IconButton
        disabled
        size="small"
        aria-label={label}
        sx={{
          '&.Mui-disabled': { color: 'rgba(255,255,255,0.35)' }
        }}
      >
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
          <LockIcon sx={{ position: 'absolute', fontSize: '9px', bottom: -1, right: -3, color: '#ff5722' }} />
        </Box>
      </IconButton>
    </span>
  </Tooltip>
);

const CanalizacionesPage = ({ section, title }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSearchTerm('');
    setLoading(true);
    setError(null);

    const site = getSite();
    fetch(`${API_BASE_URL}/canalizaciones?site=${encodeURIComponent(site)}&section=${encodeURIComponent(section)}&lang=${encodeURIComponent(i18n.language)}`)
      .then(res => {
        if (!res.ok) throw new Error('Error al cargar el contenido');
        return res.json();
      })
      .then(data => {
        setItems(data.items || []);
        setFilteredItems(data.items || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching canalizaciones:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [section]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = items.filter(item =>
        item.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.canalizador.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredItems(filtered);
    } else {
      setFilteredItems(items);
    }
  }, [searchTerm, items]);

  return (
    <Container component="main" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center"
        sx={{
          fontSize: 'calc(16px + 2vmin)',
          textAlign: 'center',
          width: '100%',
          '@media (max-width: 600px)': {
            fontSize: 'calc(14px + 2vmin)',
          }
        }}
      >
        {title}
      </Typography>

      <TextField
        fullWidth
        variant="outlined"
        placeholder={t('search.placeholder')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 3,
          "& .MuiInputBase-input": { color: "#fff" },
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: "#fff" },
            "&:hover fieldset": { borderColor: "#fff" },
            "&.Mui-focused fieldset": { borderColor: "#fff" }
          },
          "& .MuiInputBase-input::placeholder": { color: "#fff", opacity: 0.7 }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#fff' }} />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress color="secondary" />
        </Box>
      ) : error ? (
        <Box width="100%" textAlign="center" my={4}>
          <Typography variant="h6" color="error">
            {t('error.loading')}
          </Typography>
        </Box>
      ) : filteredItems.length === 0 ? (
        <Box width="100%" textAlign="center" my={4}>
          <Typography variant="h6">
            {t('list.empty')}
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {filteredItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem
                onClick={() => navigate(`/canalizacion/${item.id}`)}
                sx={{
                  px: { xs: 1, sm: 2 },
                  py: 1.5,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 1 }
                }}
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: { xs: 0, sm: 0.5 } }}>
                    <LockedAction icon={<PdfIcon fontSize="small" />} label={t('locked.pdf')} tooltip={t('locked.tooltip')} />
                    <LockedAction icon={<MusicNoteIcon fontSize="small" />} label={t('locked.music')} tooltip={t('locked.tooltip')} />
                    <LockedAction icon={<SchoolIcon fontSize="small" />} label={t('locked.teachings')} tooltip={t('locked.tooltip')} />
                    <LockedAction icon={<ChatIcon fontSize="small" />} label={t('locked.chat')} tooltip={t('locked.tooltip')} />
                  </Box>
                }
              >
                <ListItemAvatar>
                  <Avatar
                    src={item.foto || undefined}
                    alt={item.canalizador}
                    sx={{ width: 56, height: 56, mr: 1, bgcolor: '#ff5722' }}
                  >
                    {!item.foto && item.canalizador.charAt(0).toUpperCase()}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={item.nombre}
                  secondary={item.canalizador}
                  primaryTypographyProps={{
                    sx: {
                      color: '#fff',
                      fontWeight: 500,
                      fontSize: { xs: '0.85rem', sm: '0.95rem' },
                      pr: { xs: 16, sm: 20 }
                    }
                  }}
                  secondaryTypographyProps={{
                    sx: {
                      color: 'rgba(255,255,255,0.55)',
                      fontSize: { xs: '0.75rem', sm: '0.85rem' }
                    }
                  }}
                />
              </ListItem>
              {index < filteredItems.length - 1 && (
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
              )}
            </React.Fragment>
          ))}
        </List>
      )}
    </Container>
  );
};

export default CanalizacionesPage;
